import { ADDITIONAL_ENROLLMENT_STATUSES, getAdditionalEnrollmentStatuses } from 'utils/enrollmentStatuses';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Wrap,
  WrapItem,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { IoClose } from 'react-icons/io5';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import { STUDENT_SCHOOL_FILTER_OPTIONS } from 'utils/studentSchools';

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'registrado', label: 'Registrado' },
  { value: 'estudiando', label: 'Estudiando' },
  { value: 'promovido', label: 'Promovido' },
  { value: 'graduado', label: 'Graduado' },
  { value: 'retirado', label: 'Retirado' },
  ...ADDITIONAL_ENROLLMENT_STATUSES,
];

export const ACTIVE_REPORT_STATUS_OPTION = { value: 'activos', label: 'Activos (Estudiando y Promoción Pendiente)' };

const defaultValue = {
  program_type: '',
  program: '',
  group: '',
  status: '',
  semester: '',
  school: '',
};

/** Colegio (student.school) — vacío = todos */
export const SCHOOL_FILTER_OPTIONS = [
  { value: '', label: 'Todos los colegios' },
  ...STUDENT_SCHOOL_FILTER_OPTIONS,
];

/** Filtros mostrados por defecto (sin colegio; la página de reporte académico puede añadir `school` vía visibleFilters). */
const DEFAULT_FILTER_KEYS = ['program_type', 'program', 'group', 'status', 'semester'];
/** @type {('program_type'|'program'|'group'|'status'|'semester'|'school')[]} */
const ALL_FILTER_KEYS = [...DEFAULT_FILTER_KEYS, 'school'];

function SmartFilterBar({
  title = 'Filtro',
  description = '',
  value = defaultValue,
  onChange,
  loading = false,
  /** Si se pasa, solo se muestran estos filtros. Por defecto se muestran todos. */
  visibleFilters,
  includeActiveStatus = false,
}) {
  const { user } = useAuth();
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [fetching, setFetching] = useState(true);

  const getPrograms = useCallback(
    async (programTypeSlug) => {
      const programType = programTypes.find((el) => el.slug === programTypeSlug);
      if (!programType) return;
      setPrograms([]);
      setGroups([]);
      const res = await xhr(
        `/${programType.type.replace('_', '-')}s?filters[program_type][id][$eq]=${programType.id}&filters[active][$eq]=true&pagination[pageSize]=1000`
      );
      setPrograms((res.data.data || []).map((el) => ({ ...el, label: el.name, value: el.id })));
    },
    [programTypes]
  );

  const getGroupsByProgram = useCallback(
    async (programTypeSlug, programId) => {
      if (!programTypeSlug || !programId) return;
      setGroups([]);
      const filterKey = programTypeSlug === 'carreras-tecnicas' ? 'technical_career' : 'grade';
      const res = await xhr(
        `/class-groups?filters[${filterKey}][id][$eq]=${programId}&filters[active][$eq]=true`
      );
      setGroups(
        (res.data.data || []).map((el) => ({
          ...el,
          label: el.name && el.name !== '.' ? el.name : `Grupo ${el.id}`,
          value: el.id,
        }))
      );
    },
    []
  );

  useEffect(() => {
    const loadProgramTypes = async () => {
      try {
        const res = await xhr(`/program-types?filters[active][$eq]=true`);
        const data = res.data.data || [];
        const filtered = user?.school ? data.filter((el) => el.id === 4 || el.id === 5) : data;
        setProgramTypes(filtered.map((el) => ({ ...el, label: el.name, value: el.slug })));
      } finally {
        setFetching(false);
      }
    };
    loadProgramTypes();
  }, [user?.school]);

  useEffect(() => {
    const loadSemesters = async () => {
      try {
        let semUrl = '/semesters?pagination[limit]=-1';
        if (user?.school) {
          semUrl += `&filters[school][id][$eq]=${user.school.id}`;
        } else {
          semUrl += '&filters[$or][0][school][id][$null]=true&filters[$or][1][school][id][$eq]=0';
        }
        const res = await xhr(semUrl);
        setSemesters((res.data.data || []).map((el) => ({ ...el, label: el.name, value: el.id })));
      } catch (e) {
        setSemesters([]);
      }
    };
    loadSemesters();
  }, []);

  useEffect(() => {
    if (value.program_type) getPrograms(value.program_type);
    else {
      setPrograms([]);
      setGroups([]);
    }
  }, [value.program_type, getPrograms]);

  useEffect(() => {
    if (value.program_type && value.program) getGroupsByProgram(value.program_type, Number(value.program));
    else setGroups([]);
  }, [value.program_type, value.program, getGroupsByProgram]);

  const handleChange = (field, val) => {
    const next = { ...defaultValue, ...value, [field]: val };
    if (field === 'program_type') {
      next.program = '';
      next.group = '';
      const type = programTypes.find((p) => p.value === val)?.type;
      if (ADDITIONAL_ENROLLMENT_STATUSES.some((o) => o.value === next.status) &&
          !getAdditionalEnrollmentStatuses(type).some((o) => o.value === next.status)) next.status = '';
    }
    if (field === 'program') next.group = '';
    onChange(next);
  };

  const chipStyles = {
    borderRadius: 'lg',
    px: 3,
    py: 1.5,
    cursor: 'pointer',
    borderWidth: '1px',
    borderColor: 'gray.200',
    bg: 'white',
    _hover: { bg: 'gray.50' },
  };

  const labelStyles = { as: 'span', color: 'gray.600', fontSize: 'sm', mr: 2 };
  const valueStyles = { as: 'span', fontWeight: 'medium', fontSize: 'sm', color: 'gray.900' };

  const renderChip = (label, displayVal, menuContent, isDisabled = false, field = null) => {
    const merged = { ...defaultValue, ...value };
    const hasValue = field && merged[field] !== '' && merged[field] != null;
    return (
      <Flex
        key={label}
        align="center"
        {...chipStyles}
        opacity={loading || fetching || isDisabled ? 0.7 : 1}
        position="relative"
      >
        <Menu placement="bottom-start">
          <MenuButton
            as={Box}
            position="absolute"
            inset={0}
            cursor={loading || fetching || isDisabled ? 'not-allowed' : 'pointer'}
            bg="transparent"
            border="none"
            borderRadius="inherit"
            isDisabled={loading || fetching || isDisabled}
            _hover={{}}
            _active={{}}
          />
          <MenuList minW="200px" zIndex={10}>{menuContent}</MenuList>
        </Menu>
        <Flex align="center" gap={0} w="100%" pointerEvents="none">
          <Text {...labelStyles}>{label}:</Text>
          <Flex
            align="center"
            bg="gray.100"
            borderRadius="full"
            pl={hasValue ? 1 : 2.5}
            pr={2.5}
            py={0.5}
            gap={1}
          >
            {hasValue && (
              <Box
                as="button"
                type="button"
                borderRadius="full"
                p={0.5}
                color="gray.500"
                _hover={{ bg: 'gray.200', color: 'gray.700' }}
                onClick={() => handleChange(field, '')}
                aria-label={`Limpiar ${label}`}
                flexShrink={0}
                pointerEvents="auto"
                position="relative"
                zIndex={1}
              >
                <Box as={IoClose} boxSize={4} />
              </Box>
            )}
            <Text {...valueStyles}>{displayVal || '—'}</Text>
          </Flex>
          <ChevronDownIcon boxSize={4} ml={2} color="gray.500" flexShrink={0} />
        </Flex>
      </Flex>
    );
  };

  const mergedValue = { ...defaultValue, ...value };
  const programTypeLabel = programTypes.find((p) => p.value === mergedValue.program_type)?.label || mergedValue.program_type || '—';
  const programLabel = programs.find((p) => p.value === Number(mergedValue.program))?.label || mergedValue.program || '—';
  const groupLabel = groups.find((g) => g.value === Number(mergedValue.group))?.label || mergedValue.group || '—';
  const statusOptions = includeActiveStatus ? [ACTIVE_REPORT_STATUS_OPTION, ...STATUS_FILTER_OPTIONS] : STATUS_FILTER_OPTIONS;
  const statusLabel = statusOptions.find((s) => s.value === mergedValue.status)?.label || mergedValue.status || '—';
  const semesterLabel = semesters.find((s) => s.value === Number(mergedValue.semester))?.label || mergedValue.semester || '—';
  const schoolLabel = SCHOOL_FILTER_OPTIONS.find((s) => s.value === (mergedValue.school || ''))?.label || '—';

  const keysToShow = Array.isArray(visibleFilters) && visibleFilters.length > 0
    ? visibleFilters.filter((k) => ALL_FILTER_KEYS.includes(k))
    : DEFAULT_FILTER_KEYS;

  const chipConfigs = [
    {
      key: 'program_type',
      label: 'Tipo',
      displayVal: programTypeLabel,
      menuContent: programTypes.map((p) => (
        <MenuItem key={p.value} onClick={() => handleChange('program_type', p.value)}>
          {p.label}
        </MenuItem>
      )),
      isDisabled: false,
    },
    {
      key: 'program',
      label: 'Programa',
      displayVal: programLabel,
      menuContent: programs.length
        ? programs.map((p) => (
          <MenuItem key={p.value} onClick={() => handleChange('program', p.value)}>
            {p.label}
          </MenuItem>
        ))
        : [<MenuItem key="empty" isDisabled>Selecciona primero un Tipo</MenuItem>],
      isDisabled: !value.program_type,
    },
    {
      key: 'group',
      label: 'Grupo',
      displayVal: groupLabel,
      menuContent: groups.length
        ? groups.map((g) => (
          <MenuItem key={g.value} onClick={() => handleChange('group', g.value)}>
            {g.label}
          </MenuItem>
        ))
        : [<MenuItem key="empty" isDisabled>Selecciona primero un Programa</MenuItem>],
      isDisabled: !value.program,
    },
    {
      key: 'status',
      label: 'Estado',
      displayVal: statusLabel,
      menuContent: statusOptions.filter((option) =>
        !ADDITIONAL_ENROLLMENT_STATUSES.some((o) => o.value === option.value) ||
        getAdditionalEnrollmentStatuses(programTypes.find((p) => p.value === value.program_type)?.type)
          .some((o) => o.value === option.value)
      ).map((s) => (
        <MenuItem key={s.value} onClick={() => handleChange('status', s.value)}>
          {s.label}
        </MenuItem>
      )),
      isDisabled: false,
    },
    {
      key: 'semester',
      label: 'Semestre',
      displayVal: semesterLabel,
      menuContent: semesters.map((s) => (
        <MenuItem key={s.value} onClick={() => handleChange('semester', s.value)}>
          {s.label}
        </MenuItem>
      )),
      isDisabled: false,
    },
    {
      key: 'school',
      label: 'Colegio',
      displayVal: schoolLabel,
      menuContent: SCHOOL_FILTER_OPTIONS.map((s) => (
        <MenuItem key={s.value || 'all'} onClick={() => handleChange('school', s.value)}>
          {s.label}
        </MenuItem>
      )),
      isDisabled: false,
    },
  ];

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      p={5}
    >
      {(title || description) && (
        <Flex align="baseline" mb={4} columnGap={2} rowGap={1} flexWrap="wrap">
          {title && (
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              {title}
            </Text>
          )}
          {description && (
            <Text fontSize="sm" color="gray.600">
              {description}
            </Text>
          )}
        </Flex>
      )}
      {fetching ? (
        <Flex align="center" gap={2} py={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500">Cargando filtros...</Text>
        </Flex>
      ) : (
        <Wrap spacing={3} align="center">
          {chipConfigs
            .filter((c) => keysToShow.includes(c.key))
            .map((c) => (
              <WrapItem key={c.key}>
                {renderChip(c.label, c.displayVal, c.menuContent, c.isDisabled, c.key)}
              </WrapItem>
            ))}
        </Wrap>
      )}
    </Box>
  );
}

export default SmartFilterBar;
